import React, { useState, useEffect, useLayoutEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { apiGetCycles, apiGetMentals, apiGetSports } from '../services/api';
import PageHeader from '../components/PageHeader';
import { DAYS, MOOD_EMOJI } from '../constants/labels';
import { PHASE, MENTAL, SPORT, UI } from '../constants/colors';
import './Calendrier.css';
import { todayISO } from '../utils/formDate';
import { buildDetailRows } from '../constants/dayDetails';
const MONTHS_FR = [
  'Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin',
  'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre',
];

const WEEKDAYS_FR = [
  'Dimanche', 'Lundi', 'Mardi', 'Mercredi', 'Jeudi', 'Vendredi', 'Samedi',
];


const FILTERS = [
  { id: 'cycle',  label: '🩸 Cycle' },
  { id: 'mental', label: '🧠 Mental' },
  { id: 'sport',  label: '🏃 Sport' },
];

// Anneaux concentriques d'une case : ordre intérieur → extérieur identique
// au donut de l'accueil (cycle au centre, puis mental, puis sport).
const CELL_RING_ORDER = ['cycle', 'mental', 'sport'];
const CELL_RING_RADII = { cycle: 7, mental: 13.5, sport: 20 };
const CELL_RING_STROKE = 4.5;

// Sections de la fiche « détail du jour » (même ordre que les anneaux).
const DETAIL_SECTIONS = [
  { id: 'cycle',  title: 'Cycle',  icon: '🩸', color: PHASE.primary  },
  { id: 'mental', title: 'Mental', icon: '🧠', color: MENTAL.primary },
  { id: 'sport',  title: 'Sport',  icon: '🏃', color: SPORT.primary  },
];

const Calendrier = () => {
  const { user, getToken } = useAuth();
  const navigate = useNavigate();
  const [cycles,  setCycles]  = useState([]);
  const [mentals, setMentals] = useState([]);
  const [sports,  setSports]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState(['cycle', 'mental', 'sport']);
  const [extraMonths, setExtraMonths] = useState(0);
  const [selectedDate, setSelectedDate] = useState(null);

  // refs for scroll management
  const currentMonthRef   = useRef(null);
  const topSentinelRef    = useRef(null);
  const pendingAdjust     = useRef(null);
  const hasScrolled       = useRef(false);

  useEffect(() => {
    if (!user?.id) { setLoading(false); return; }
    const token = getToken();
    Promise.all([
      apiGetCycles(user.id, token),
      apiGetMentals(user.id, token),
      apiGetSports(user.id, token),
    ]).then(([c, m, s]) => {
      setCycles(c);
      setMentals(m);
      setSports(s);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [user?.id, getToken]);

  const cycleByDate  = useMemo(() => Object.fromEntries(cycles.map(e => [e.date, e])),  [cycles]);
  const mentalByDate = useMemo(() => Object.fromEntries(mentals.map(e => [e.date, e])), [mentals]);
  const sportByDate  = useMemo(() => Object.fromEntries(sports.map(e => [e.date, e])),  [sports]);

  // mois courant + historique extensible vers le passé
  const months = useMemo(() => {
    const today = new Date();
    const total = 6 + extraMonths;
    return Array.from({ length: total }, (_, i) => {
      const d = new Date(today.getFullYear(), today.getMonth() - (total - 1 - i), 1);
      return { year: d.getFullYear(), month: d.getMonth() };
    });
  }, [extraMonths]);

  // Scroll initial vers le mois courant (une seule fois après le chargement)
  useEffect(() => {
    if (!loading && !hasScrolled.current && currentMonthRef.current) {
      hasScrolled.current = true;
      requestAnimationFrame(() => {
        currentMonthRef.current?.scrollIntoView({ behavior: 'instant', block: 'start' });
      });
    }
  }, [loading]);

  // Ajuster la position de scroll après ajout de mois en haut (sans saut visuel)
  useLayoutEffect(() => {
    if (pendingAdjust.current) {
      const { scrollY, prevHeight } = pendingAdjust.current;
      const delta = document.documentElement.scrollHeight - prevHeight;
      window.scrollTo({ top: scrollY + delta, behavior: 'instant' });
      pendingAdjust.current = null;
    }
  });

  const loadMoreMonths = useCallback(() => {
    pendingAdjust.current = {
      scrollY: window.scrollY,
      prevHeight: document.documentElement.scrollHeight,
    };
    setExtraMonths(prev => prev + 3);
  }, []);

  // IntersectionObserver sur le sentinel en haut de liste
  useEffect(() => {
    if (loading || !topSentinelRef.current) return;
    const sentinel = topSentinelRef.current;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          loadMoreMonths();
        }
      },
      { rootMargin: '300px 0px 0px 0px', threshold: 0 }
    );
    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [loading, extraMonths, loadMoreMonths]);

  const today = todayISO();

  // Fermeture de la fiche détail à la touche Échap
  useEffect(() => {
    if (!selectedDate) return;
    const onKeyDown = (e) => { if (e.key === 'Escape') setSelectedDate(null); };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [selectedDate]);

  const toggleFilter = useCallback((id) => {
    setFilters(prev =>
      prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
    );
  }, []);

  // Retourne les infos d'un jour pour un filtre donné
  const getDayInfo = useCallback((dateStr, filterId) => {
    if (filterId === 'cycle') {
      const e = cycleByDate[dateStr];
      if (!e) return null;
      if (e.is_rest_day)    return { bg: UI.restDayBg,           dot: UI.textAxis };
      if (e.flow)           return { bg: PHASE.regles,           dot: PHASE.regles,     dark: true };
      if (e.ovulation_test) return { bg: PHASE.ovulation,        dot: PHASE.ovulation };
      return                       { bg: PHASE.folliculaire,      dot: PHASE.folliculaire };
    }
    if (filterId === 'mental') {
      const e = mentalByDate[dateStr];
      if (!e) return null;
      const emoji = e.is_rest_day ? '—' : (e.mood ? (MOOD_EMOJI[e.mood] ?? '😊') : '😊');
      return { bg: MENTAL.concentration + '33', dot: MENTAL.concentration, label: emoji };
    }
    if (filterId === 'sport') {
      const e = sportByDate[dateStr];
      if (!e) return null;
      if (e.is_rest_day) return { bg: SPORT.rpeFacile + '44', dot: SPORT.rpeFacile, label: '💤' };
      if (e.rpe != null) {
        const intense = e.rpe > 5;
        const color   = intense ? SPORT.rpeIntense : SPORT.rpeFacile;
        return { bg: color + (intense ? '55' : '44'), dot: color, label: String(e.rpe), dark: intense };
      }
      return null;
    }
    return null;
  }, [cycleByDate, mentalByDate, sportByDate]);

  return (
    <div className="cal-page">
      <PageHeader title="Calendrier" />

      {/* ── Filtres ── */}
      <div className="cal-page__filters">
        {FILTERS.map(f => (
          <button
            key={f.id}
            className={`cal-page__filter-btn${filters.includes(f.id) ? ' cal-page__filter-btn--active' : ''}`}
            onClick={() => toggleFilter(f.id)}
          >
            {f.label}
          </button>
        ))}
      </div>

      {/* ── Légende ── */}
      <div className="cal-page__legend">
        {filters.includes('cycle') && (
          <>
            <span className="cal-page__legend-item">
              <span className="cal-page__legend-dot" style={{ background: PHASE.regles }} />Règles
            </span>
            <span className="cal-page__legend-item">
              <span className="cal-page__legend-dot" style={{ background: PHASE.ovulation }} />Ovulation
            </span>
            <span className="cal-page__legend-item">
              <span className="cal-page__legend-dot" style={{ background: PHASE.folliculaire }} />Folliculaire
            </span>
          </>
        )}
        {filters.includes('mental') && (
          <>
            <span className="cal-page__legend-item">
              <span className="cal-page__legend-dot" style={{ background: MENTAL.concentration }} />Humeur
            </span>
          </>
        )}
        {filters.includes('sport') && (
          <>
            <span className="cal-page__legend-item">
              <span className="cal-page__legend-dot" style={{ background: SPORT.rpeFacile }} />Modéré (RPE ≤ 5)
            </span>
            <span className="cal-page__legend-item">
              <span className="cal-page__legend-dot" style={{ background: SPORT.rpeIntense }} />Intense (RPE &gt; 5)
            </span>
          </>
        )}
      </div>

      {/* ── Mois ── */}
      {loading ? (
        <p className="cal-page__loading">Chargement…</p>
      ) : (
        <div className="cal-page__months">
          {/* sentinel invisible : quand il entre dans le viewport, on charge plus de mois */}
          <div ref={topSentinelRef} style={{ height: 1 }} />

          {months.map(({ year, month }, idx) => {
            const firstDow = new Date(year, month, 1).getDay(); // 0=Sun
            const offset   = (firstDow + 6) % 7;               // Mon=0
            const daysInMonth = new Date(year, month + 1, 0).getDate();
            const isCurrentMonth = idx === months.length - 1;

            const cells = [
              ...Array(offset).fill(null),
              ...Array.from({ length: daysInMonth }, (_, i) => i + 1),
            ];
            while (cells.length % 7 !== 0) cells.push(null);

            return (
              <div
                key={`${year}-${month}`}
                className="cal-page__month"
                ref={isCurrentMonth ? currentMonthRef : null}
              >
                <h3 className="cal-page__month-title">
                  {MONTHS_FR[month]} {year}
                </h3>

                <div className="cal-page__grid-header">
                  {DAYS.map((d, i) => <span key={i}>{d}</span>)}
                </div>

                <div className="cal-page__grid">
                  {cells.map((day, i) => {
                    if (!day) return <div key={i} className="cal-page__cell cal-page__cell--empty" />;

                    const dateStr = `${year}-${String(month + 1).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
                    const isToday = dateStr === today;

                    // Chaque catégorie active possédant une donnée du jour
                    // devient un anneau concentrique de couleur unie.
                    const rings = CELL_RING_ORDER
                      .filter(f => filters.includes(f))
                      .map(f => ({ f, info: getDayInfo(dateStr, f) }))
                      .filter(x => x.info);

                    return (
                      <button
                        key={i}
                        type="button"
                        className={`cal-page__cell${isToday ? ' cal-page__cell--today' : ''}`}
                        onClick={() => setSelectedDate(dateStr)}
                        aria-label={`Détail du ${day} ${MONTHS_FR[month]} ${year}`}
                      >
                        <span className="cal-page__cell-rings-wrap">
                          {rings.length > 0 && (
                            <svg className="cal-page__cell-rings" viewBox="0 0 48 48" aria-hidden="true">
                              {rings.map(({ f, info }) => (
                                <circle
                                  key={f}
                                  cx="24"
                                  cy="24"
                                  r={CELL_RING_RADII[f]}
                                  fill="none"
                                  stroke={info.dot}
                                  strokeWidth={CELL_RING_STROKE}
                                />
                              ))}
                            </svg>
                          )}
                        </span>
                        <span className="cal-page__cell-num">{day}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ── Fiche détail du jour ── */}
      {selectedDate && (() => {
        const [y, m, d] = selectedDate.split('-').map(Number);
        const dateObj   = new Date(y, m - 1, d);
        const dateLabel = `${WEEKDAYS_FR[dateObj.getDay()]} ${d} ${MONTHS_FR[m - 1].toLowerCase()} ${y}`;
        const entries   = { cycle: cycleByDate, mental: mentalByDate, sport: sportByDate };
        const isFuture  = selectedDate > today;
        const hasAny    = DETAIL_SECTIONS.some(s => entries[s.id][selectedDate]);

        return (
          <div className="cal-page__day-overlay" onClick={() => setSelectedDate(null)}>
            <div
              className="cal-page__day-modal"
              role="dialog"
              aria-modal="true"
              aria-label={`Détail du ${dateLabel}`}
              onClick={e => e.stopPropagation()}
            >
              <div className="cal-page__day-modal-header">
                <span className="cal-page__day-modal-date">{dateLabel}</span>
                <button
                  className="cal-page__day-modal-close"
                  onClick={() => setSelectedDate(null)}
                  aria-label="Fermer"
                >✕</button>
              </div>

              <div className="cal-page__day-modal-body">
                {DETAIL_SECTIONS.map(({ id, title, icon, color }) => {
                  const entry = entries[id][selectedDate];
                  const rows  = entry && !entry.is_rest_day ? buildDetailRows(id, entry) : [];

                  return (
                    <section key={id} className="cal-page__day-section">
                      <h4 className="cal-page__day-section-title" style={{ color }}>
                        <span aria-hidden="true">{icon}</span> {title}
                      </h4>

                      {!entry ? (
                        <p className="cal-page__day-empty">Aucune donnée</p>
                      ) : entry.is_rest_day ? (
                        <p className="cal-page__day-empty">Jour de repos</p>
                      ) : (
                        <>
                          {rows.length > 0 ? (
                            <div className="cal-page__day-rows">
                              {rows.map(([label, value]) => (
                                <div key={label} className="cal-page__day-row">
                                  <span>{label}</span>
                                  <span>{value}</span>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <p className="cal-page__day-empty">Aucun détail renseigné</p>
                          )}
                          {entry.notes && (
                            <p className="cal-page__day-note">{entry.notes}</p>
                          )}
                        </>
                      )}
                    </section>
                  );
                })}
              </div>

              {!isFuture && (
                <button
                  className="cal-page__day-modal-btn"
                  onClick={() => {
                    const date = selectedDate;
                    setSelectedDate(null);
                    navigate(`/formulaire/cycle?date=${date}`);
                  }}
                >
                  {hasAny ? 'Modifier les données' : 'Ajouter des données'}
                </button>
              )}
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default Calendrier;
